import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MobileShell } from "@/components/MobileShell";
import { useAuthSession } from "@/context/AuthSessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMAIL_KEY = "storefront_pending_login_identifier";
const REDIRECT_KEY = "storefront_pending_login_redirect";

export default function AccountEmail() {
    const navigate = useNavigate();
    const location = useLocation();
    const { sendOtp } = useAuthSession();
    const [identifier, setIdentifier] = useState(() => sessionStorage.getItem(EMAIL_KEY) || "");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");
    const redirectTo = location.state?.redirectTo || sessionStorage.getItem(REDIRECT_KEY) || "/account";

    const submit = async (e) => {
        e.preventDefault();
        const trimmed = identifier.trim();
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setError("Please enter a valid email address.");
            return;
        }

        setPending(true);
        setError("");
        try {
            await sendOtp(trimmed);
            sessionStorage.setItem(EMAIL_KEY, trimmed);
            sessionStorage.setItem(REDIRECT_KEY, redirectTo);
            toast.success("Verification code sent.");
            navigate("/account/verify", { state: { identifier: trimmed, redirectTo } });
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not send verification code.");
            toast.error(err?.response?.data?.detail || "Could not send verification code");
        } finally {
            setPending(false);
        }
    };

    return (
        <MobileShell showCart={false}>
            <div className="pt-6 md:pt-10 max-w-xl mx-auto space-y-6" data-testid="account-email-page">
                <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Account access</p>
                    <h1 className="font-serif text-4xl md:text-5xl">Enter your email</h1>
                    <p className="text-sm text-muted-foreground">
                        We’ll send a 6-digit verification code to continue.
                    </p>
                </div>

                <form onSubmit={submit} className="rounded-3xl border border-border/60 bg-surface/30 p-5 space-y-4">
                    <div>
                        <Label htmlFor="login-email" className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
                            Email address
                        </Label>
                        <Input
                            id="login-email"
                            data-testid="login-email-input"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            placeholder="you@example.com"
                            className={`mt-2 h-12 rounded-2xl border-2 ${error ? "border-destructive" : "focus:border-primary"}`}
                        />
                        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
                    </div>

                    <Button
                        type="submit"
                        disabled={pending}
                        className="w-full h-12 rounded-full"
                        data-testid="send-verification-code-button"
                    >
                        {pending ? "Sending..." : "Send verification code"}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                        Already verified? <Link to="/account" className="text-primary hover:underline">Go to profile</Link>
                    </p>
                </form>
            </div>
        </MobileShell>
    );
}